import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { MeResponse } from "../types";
import { PeriodPicker } from "../components/PeriodPicker";

interface AgentRow {
  deal_agent_id: number;
  deal_id: number;
  deal_date: string | null;
  agent_name: string;
  counterparty: string | null;
  scheme: string;
  rate: number | null;
  fixed_amount: number | null;
  currency: string | null;
  base_rub: number;
  accrued: boolean;
  accrued_amount: number | null;
  paid: boolean;
}

interface ReportResponse {
  rows: AgentRow[];
  totals: {
    count: number;
    total_base_rub: number;
    total_accrued_rub: number;
    total_paid_rub: number;
    avg_rate_pct: number;
  };
  period: { start: string; end: string };
}

const SCHEME_LABEL: Record<string, string> = {
  percent_fixed: "% фикс",
  percent_variable: "% перем.",
  percent_per_tx_manual: "% вручную",
  fixed_per_tx: "фикс-сумма",
  percent_turnover: "% оборот",
};

function fmt(n: number): string {
  return n.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

export function AgentsPage({ me }: { me: MeResponse }) {
  const [period, setPeriod] = useState("this_month");
  const [customStart, setCustomStart] = useState<string | null>(null);
  const [customEnd, setCustomEnd] = useState<string | null>(null);
  const [data, setData] = useState<ReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ periodKey: period });
    if (period === "custom" && customStart && customEnd) {
      p.set("customStart", customStart);
      p.set("customEnd", customEnd);
    }
    return p.toString();
  }, [period, customStart, customEnd]);

  useEffect(() => {
    if (period === "custom" && !(customStart && customEnd)) return;
    setLoading(true);
    setError(null);
    api
      .get<ReportResponse>(`/api/agents/report?${qs}`)
      .then(setData)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [qs, period, customStart, customEnd]);

  const t = data?.totals;

  // Безопасность: раздел только для админа (хуки выше выполнены безусловно).
  if (!me.permissions.agents_view) {
    return (
      <div className="text-tg-hint text-sm">
        Раздел доступен только администратору.
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold mb-3">🤝 Агенты-комиссионеры</h1>

      <div className="mb-4">
        <label className="text-xs text-tg-hint">Период</label>
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(s, e) => {
            setCustomStart(s);
            setCustomEnd(e);
          }}
        />
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {t && (
        <section className="bg-tg-sectionBg rounded-xl p-4 mb-4">
          <div className="text-xs text-tg-hint">Итоги за период</div>
          <div className="mt-1 text-2xl font-semibold">
            {fmt(t.total_accrued_rub)} ₽
          </div>
          <div className="text-sm text-tg-hint">
            начислено · выплачено {fmt(t.total_paid_rub)} ₽
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <Tile label="Заявок" value={String(t.count)} />
            <Tile label="Тело (база)" value={`${fmt(t.total_base_rub)} ₽`} />
            <Tile label="Средний %" value={`${t.avg_rate_pct.toFixed(3)}%`} />
          </div>
        </section>
      )}

      {loading && <div className="text-tg-hint text-sm animate-pulse">Загрузка…</div>}

      <div className="space-y-2">
        {data?.rows.map((r) => (
          <div key={r.deal_agent_id} className="bg-tg-sectionBg rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm">{r.agent_name}</div>
              <div className="text-xs text-tg-hint">
                заявка №{r.deal_id}
                {r.deal_date ? ` · ${r.deal_date}` : ""}
              </div>
            </div>
            <div className="text-xs text-tg-hint mt-0.5">
              {r.counterparty || "—"} · {SCHEME_LABEL[r.scheme] || r.scheme} ·{" "}
              {r.scheme === "fixed_per_tx"
                ? `${r.fixed_amount ?? 0} ${r.currency ?? ""}`
                : `${r.rate ?? 0}%`}
            </div>
            <div className="text-sm mt-1">
              база {fmt(r.base_rub)} ₽ ·{" "}
              {r.accrued && r.accrued_amount != null ? (
                <span className="text-emerald-600 font-medium">
                  начислено {fmt(r.accrued_amount)}{" "}
                  {r.scheme === "fixed_per_tx" ? r.currency ?? "" : "₽"}
                </span>
              ) : (
                <span className="text-tg-hint">ещё не начислено</span>
              )}
              {r.paid && <span className="ml-1">· ✅ выплачено</span>}
            </div>
          </div>
        ))}
        {data && data.rows.length === 0 && !loading && (
          <div className="text-tg-hint text-sm">
            За период начислений по агентам нет.
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-tg-bg rounded-lg p-2">
      <div className="text-tg-hint">{label}</div>
      <div className="font-medium mt-0.5 truncate">{value}</div>
    </div>
  );
}
